import React, { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { X, Sliders, LayoutTemplate } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DashboardOverlayContent } from './DashboardOverlayContent';

interface ControlsOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  controlsDashboardId?: string | null;
}

export const ControlsOverlay: React.FC<ControlsOverlayProps> = ({
  isOpen,
  onClose,
  controlsDashboardId,
}) => {
  const navigate = useNavigate();

  const handleCloseClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    },
    [onClose]
  );

  if (!isOpen) return null;

  const content = (
    <div
      className="fixed inset-0 z-[100] flex justify-center pointer-events-none"
    >
      {/* Panel - frosted glass / glassmorphism style */}
      <div
        className={cn(
          'pointer-events-auto w-full max-w-2xl mx-4 mt-0 h-[30vh] min-h-0 overflow-hidden rounded-b-xl',
          'backdrop-blur-xl',
          'animate-in slide-in-from-top duration-300'
        )}
        style={{
          background: 'rgba(255, 255, 255, 0.25)',
        }}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Sliders className="h-5 w-5" />
              Controls
            </h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleCloseClick}
              className="shrink-0 z-10"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Body - no scroll (vertical or horizontal), fit within 30vh */}
          <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
            <div className="flex-1 min-h-0 min-w-0 overflow-hidden p-4">
              {controlsDashboardId ? (
                <DashboardOverlayContent
                  dashboardId={controlsDashboardId}
                  emptyMessage="No widgets yet. Add components in the Dashboard Builder."
                  className="mb-3"
                  compact
                  glass
                />
              ) : (
                <p className="text-sm text-muted-foreground mb-3">
                  Select a Controls dashboard in User Settings to display it here. Or configure dashboards in the Dashboard Builder.
                </p>
              )}
            </div>

            <Button
              variant="ghost"
              className="w-full shrink-0 rounded-none py-3 bg-transparent text-muted-foreground hover:bg-transparent hover:text-foreground"
              onClick={() => navigate('/experiments/dashboards')}
            >
              <LayoutTemplate className="h-4 w-4 mr-2" />
              Edit Widgets
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};
